import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { EmptyState, PageLoader } from '@/components/ui';
import { useAuth } from '@/lib/auth';

/**
 * Admin-only gate. An unauthenticated visitor is sent to /login carrying the
 * destination; a signed-in non-admin gets a plain refusal instead of a silent
 * bounce, because silently landing on the dashboard reads as a bug.
 */
export function RequireAdmin() {
  const location = useLocation();
  const hydrated = useAuth((state) => state.hydrated);
  const authenticated = useAuth((state) => Boolean(state.user && state.tokens));
  const isAdmin = useAuth((state) => state.user?.role === 'admin');

  if (!hydrated) return <PageLoader label="Verificando permissões…" />;

  if (!authenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace state={{ from: location }} />;
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full max-w-2xl px-gutter py-16">
        <EmptyState
          icon={ShieldAlert}
          title="Área restrita"
          titleAs="h1"
          message="Esta parte do sistema é só para administradores. Se você acha que deveria ter acesso, fale com quem administra a plataforma."
          kerb
          action={
            <a
              href="/"
              className="inline-flex min-h-11 items-center rounded-md border border-border bg-surface-2 px-4 text-base font-semibold text-text"
            >
              Voltar ao início
            </a>
          }
        />
      </div>
    );
  }

  return <Outlet />;
}

export default RequireAdmin;
